/**
 * Worker Time-Series Charts Component
 *
 * Interactive charts for Cloudflare Worker performance metrics with:
 * - Time range selector (zoom: 1h, 6h, 24h, 7d)
 * - Multi-percentile lines (P50, P90, P99, P999)
 * - Hover tooltips with datetime and all values
 * - Legend with current summary values
 */

import { useState, useEffect, useCallback } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { getStatsWorkersTimeseries } from "../api/adminApi";
import type {
  StatsWorkersTimeseriesResponse,
  WorkerTimeSeriesPoint,
  WorkerTimeSeriesSummary,
} from "../types/admin";

// Time range options for zoom
const TIME_RANGES = [
  { label: "1h", hours: 1 },
  { label: "6h", hours: 6 },
  { label: "24h", hours: 24 },
  { label: "7d", hours: 168 },
];

// Percentile line colors (matching Cloudflare dashboard style)
const PERCENTILE_COLORS = {
  P50: "#3b82f6", // Blue
  P90: "#10b981", // Green
  P99: "#f59e0b", // Amber
  P999: "#ef4444", // Red
};

interface ChartDataPoint {
  datetime: string;
  formattedTime: string;
  cpuTimeP50: number;
  cpuTimeP90: number;
  cpuTimeP99: number;
  cpuTimeP999: number;
  wallTimeP50: number;
  wallTimeP90: number;
  wallTimeP99: number;
  wallTimeP999: number;
  durationP50: number;
  durationP90: number;
  durationP99: number;
  durationP999: number;
  requests: number;
  errors: number;
}

/**
 * Custom tooltip component for the charts
 */
function CustomTooltip({
  active,
  payload,
  label,
  metricType,
}: {
  active?: boolean;
  payload?: Array<{ color: string; name: string; value: number }>;
  label?: string;
  metricType: "cpu" | "wall" | "duration";
}) {
  if (!active || !payload || payload.length === 0) return null;

  const metricLabels = {
    cpu: "CPU Time",
    wall: "Wall Time",
    duration: "Duration",
  };

  return (
    <div
      style={{
        backgroundColor: "#1e293b",
        border: "1px solid #374151",
        borderRadius: "0.5rem",
        padding: "0.75rem",
        fontSize: "0.875rem",
        boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.3)",
      }}
    >
      <div style={{ color: "#94a3b8", marginBottom: "0.5rem", fontWeight: 600 }}>
        {label}
      </div>
      <div style={{ color: "#64748b", marginBottom: "0.5rem", fontSize: "0.75rem" }}>
        {metricLabels[metricType]}
      </div>
      {payload.map((entry, index) => (
        <div
          key={index}
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: "1rem",
            color: entry.color,
          }}
        >
          <span>{entry.name}:</span>
          <span style={{ fontWeight: 600, fontFamily: "monospace" }}>
            {entry.value.toFixed(2)}ms
          </span>
        </div>
      ))}
    </div>
  );
}

/**
 * Legend with current summary values
 */
function SummaryLegend({
  summary,
  metricType,
}: {
  summary: WorkerTimeSeriesSummary;
  metricType: "cpu" | "wall" | "duration";
}) {
  const metrics =
    metricType === "cpu"
      ? {
          P50: summary.cpuTimeP50,
          P90: summary.cpuTimeP90,
          P99: summary.cpuTimeP99,
          P999: summary.cpuTimeP999,
        }
      : metricType === "wall"
        ? {
            P50: summary.wallTimeP50,
            P90: summary.wallTimeP90,
            P99: summary.wallTimeP99,
            P999: summary.wallTimeP999,
          }
        : {
            P50: summary.durationP50,
            P90: summary.durationP90,
            P99: summary.durationP99,
            P999: summary.durationP999,
          };

  return (
    <div
      style={{
        display: "flex",
        gap: "1.5rem",
        justifyContent: "center",
        marginTop: "0.5rem",
        flexWrap: "wrap",
      }}
    >
      {Object.entries(metrics).map(([percentile, value]) => (
        <div
          key={percentile}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
          }}
        >
          <div
            style={{
              width: "12px",
              height: "12px",
              borderRadius: "2px",
              backgroundColor: PERCENTILE_COLORS[percentile as keyof typeof PERCENTILE_COLORS],
            }}
          />
          <span style={{ color: "#94a3b8", fontSize: "0.75rem" }}>{percentile}:</span>
          <span
            style={{
              fontWeight: 600,
              fontFamily: "monospace",
              fontSize: "0.875rem",
              color: PERCENTILE_COLORS[percentile as keyof typeof PERCENTILE_COLORS],
            }}
          >
            {value.toFixed(2)}ms
          </span>
        </div>
      ))}
    </div>
  );
}

/**
 * Single metric chart component
 */
function MetricChart({
  data,
  summary,
  title,
  metricType,
}: {
  data: ChartDataPoint[];
  summary: WorkerTimeSeriesSummary;
  title: string;
  metricType: "cpu" | "wall" | "duration";
}) {
  const dataKeys =
    metricType === "cpu"
      ? ["cpuTimeP50", "cpuTimeP90", "cpuTimeP99", "cpuTimeP999"]
      : metricType === "wall"
        ? ["wallTimeP50", "wallTimeP90", "wallTimeP99", "wallTimeP999"]
        : ["durationP50", "durationP90", "durationP99", "durationP999"];

  const percentileLabels = ["P50", "P90", "P99", "P999"];

  return (
    <div style={{ marginBottom: "2rem" }}>
      <h4
        style={{
          margin: "0 0 0.5rem",
          fontSize: "1rem",
          color: "#f1f5f9",
          fontWeight: 500,
        }}
      >
        {title}
      </h4>
      <div style={{ height: "250px" }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
            <XAxis
              dataKey="formattedTime"
              stroke="#64748b"
              tick={{ fill: "#94a3b8", fontSize: 11 }}
              tickLine={{ stroke: "#374151" }}
            />
            <YAxis
              stroke="#64748b"
              tick={{ fill: "#94a3b8", fontSize: 11 }}
              tickLine={{ stroke: "#374151" }}
              tickFormatter={(value) => `${value}ms`}
            />
            <Tooltip content={<CustomTooltip metricType={metricType} />} />
            <Legend wrapperStyle={{ display: "none" }} />
            {dataKeys.map((key, index) => (
              <Line
                key={key}
                type="monotone"
                dataKey={key}
                name={percentileLabels[index]}
                stroke={PERCENTILE_COLORS[percentileLabels[index] as keyof typeof PERCENTILE_COLORS]}
                strokeWidth={index === 0 ? 2 : 1.5}
                dot={false}
                activeDot={{ r: 4, strokeWidth: 0 }}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
      <SummaryLegend summary={summary} metricType={metricType} />
    </div>
  );
}

/**
 * Main Worker Time-Series Charts Container
 */
export function WorkerTimeSeriesCharts() {
  const [selectedRange, setSelectedRange] = useState(24); // Default to 24h
  const [data, setData] = useState<StatsWorkersTimeseriesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getStatsWorkersTimeseries(selectedRange);
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load time-series data");
    } finally {
      setLoading(false);
    }
  }, [selectedRange]);

  useEffect(() => {
    loadData();
    // Auto-refresh every 60 seconds for time-series (less frequent than health stats)
    const interval = setInterval(loadData, 60000);
    return () => clearInterval(interval);
  }, [loadData]);

  // Transform data for charts
  const chartData: ChartDataPoint[] =
    data?.timeseries?.dataPoints.map((point: WorkerTimeSeriesPoint) => ({
      datetime: point.datetime,
      formattedTime: formatDateTime(point.datetime, selectedRange),
      cpuTimeP50: point.cpuTimeP50,
      cpuTimeP90: point.cpuTimeP90,
      cpuTimeP99: point.cpuTimeP99,
      cpuTimeP999: point.cpuTimeP999,
      wallTimeP50: point.wallTimeP50,
      wallTimeP90: point.wallTimeP90,
      wallTimeP99: point.wallTimeP99,
      wallTimeP999: point.wallTimeP999,
      durationP50: point.durationP50,
      durationP90: point.durationP90,
      durationP99: point.durationP99,
      durationP999: point.durationP999,
      requests: point.requests,
      errors: point.errors,
    })) || [];

  if (loading) {
    return (
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Worker Performance Charts</h3>
        </div>
        <div
          className="card-body"
          style={{ display: "flex", justifyContent: "center", padding: "3rem" }}
        >
          <div className="loading-spinner" style={{ width: "32px", height: "32px" }} />
        </div>
      </div>
    );
  }

  if (error || !data?.available || !data?.timeseries) {
    return (
      <div className="card" style={{ opacity: 0.7 }}>
        <div className="card-header">
          <h3 className="card-title">Worker Performance Charts</h3>
        </div>
        <div className="card-body">
          <div style={{ textAlign: "center", color: "#94a3b8", padding: "2rem" }}>
            <div style={{ marginBottom: "0.5rem" }}>📊 Time-series data unavailable</div>
            <div style={{ fontSize: "0.75rem", color: "#64748b" }}>
              {error || data?.error || "CF_API_TOKEN not configured"}
            </div>
            <button
              onClick={loadData}
              style={{
                marginTop: "1rem",
                padding: "0.5rem 1rem",
                borderRadius: "0.375rem",
                border: "1px solid #374151",
                backgroundColor: "#1e293b",
                color: "#f1f5f9",
                cursor: "pointer",
              }}
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  const { summary, resolution, period } = data.timeseries;

  return (
    <div className="card">
      <div
        className="card-header"
        style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
      >
        <div>
          <h3 className="card-title">Worker Performance Charts</h3>
          <div style={{ color: "#64748b", fontSize: "0.75rem", marginTop: "0.25rem" }}>
            Resolution: {resolution} • {new Date(period.start).toLocaleString()} -{" "}
            {new Date(period.end).toLocaleString()}
          </div>
        </div>
        {/* Time Range Selector */}
        <div style={{ display: "flex", gap: "0.5rem" }}>
          {TIME_RANGES.map((range) => (
            <button
              key={range.hours}
              onClick={() => setSelectedRange(range.hours)}
              style={{
                padding: "0.375rem 0.75rem",
                borderRadius: "0.375rem",
                border:
                  selectedRange === range.hours ? "1px solid var(--accent-primary)" : "1px solid #374151",
                backgroundColor: selectedRange === range.hours ? "var(--accent-primary)" : "#1e293b",
                color: selectedRange === range.hours ? "white" : "#94a3b8",
                cursor: "pointer",
                fontSize: "0.875rem",
                fontWeight: selectedRange === range.hours ? 600 : 400,
                transition: "all 0.15s ease",
              }}
            >
              {range.label}
            </button>
          ))}
        </div>
      </div>
      <div className="card-body">
        {/* Summary Statistics Row */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: "1rem",
            marginBottom: "2rem",
            padding: "1rem",
            backgroundColor: "#1e293b",
            borderRadius: "0.5rem",
          }}
        >
          <div style={{ textAlign: "center" }}>
            <div style={{ color: "#64748b", fontSize: "0.75rem", marginBottom: "0.25rem" }}>
              Total Requests
            </div>
            <div style={{ fontSize: "1.5rem", fontWeight: 700, color: "var(--accent-primary)" }}>
              {formatNumber(summary.totalRequests)}
            </div>
          </div>
          <div style={{ textAlign: "center" }}>
            <div style={{ color: "#64748b", fontSize: "0.75rem", marginBottom: "0.25rem" }}>
              Total Errors
            </div>
            <div
              style={{
                fontSize: "1.5rem",
                fontWeight: 700,
                color:
                  summary.totalErrors > 0
                    ? "var(--accent-danger)"
                    : "var(--accent-success)",
              }}
            >
              {summary.totalErrors}
            </div>
          </div>
          <div style={{ textAlign: "center" }}>
            <div style={{ color: "#64748b", fontSize: "0.75rem", marginBottom: "0.25rem" }}>
              Error Rate
            </div>
            <div
              style={{
                fontSize: "1.5rem",
                fontWeight: 700,
                color:
                  summary.errorRate > 0.01
                    ? "var(--accent-danger)"
                    : "var(--accent-success)",
              }}
            >
              {(summary.errorRate * 100).toFixed(3)}%
            </div>
          </div>
          <div style={{ textAlign: "center" }}>
            <div style={{ color: "#64748b", fontSize: "0.75rem", marginBottom: "0.25rem" }}>
              Data Points
            </div>
            <div style={{ fontSize: "1.5rem", fontWeight: 700, color: "#94a3b8" }}>
              {chartData.length}
            </div>
          </div>
        </div>

        {/* Charts */}
        <MetricChart
          data={chartData}
          summary={summary}
          title="CPU Time"
          metricType="cpu"
        />
        <MetricChart
          data={chartData}
          summary={summary}
          title="Wall Time"
          metricType="wall"
        />
        <MetricChart
          data={chartData}
          summary={summary}
          title="Request Duration"
          metricType="duration"
        />
      </div>
    </div>
  );
}

/**
 * Format datetime based on time range for readability
 */
function formatDateTime(datetime: string, hoursRange: number): string {
  const date = new Date(datetime);
  if (hoursRange <= 6) {
    // Short range: show HH:MM
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } else if (hoursRange <= 24) {
    // Medium range: show HH:MM
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } else {
    // Long range: show MM/DD HH:MM
    return date.toLocaleDateString([], { month: "numeric", day: "numeric" }) +
      " " +
      date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  }
}

/**
 * Format large numbers for display
 */
function formatNumber(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

export default WorkerTimeSeriesCharts;
